import React, { useRef, useState } from "react";
import { searchPlaces } from "../lib/geocode";
import { colors, fonts, shadows } from "../theme";

export default function LocationSearch({ onSelect }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const timer = useRef(null);

  const onChange = (e) => {
    const q = e.target.value;
    setQuery(q);
    clearTimeout(timer.current);
    if (q.trim().length < 2) {
      setResults([]);
      setOpen(false);
      return;
    }
    setLoading(true);
    timer.current = setTimeout(async () => {
      try {
        const rows = await searchPlaces(q);
        setResults(rows);
        setOpen(true);
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 350);
  };

  const pick = (r) => {
    onSelect({ lat: r.lat, lng: r.lng, name: shortName(r.name) });
    setQuery(shortName(r.name));
    setOpen(false);
  };

  return (
    <div style={styles.wrap} data-testid="location-search">
      <div style={styles.inputWrap}>
        <span style={styles.searchIcon}>⌕</span>
        <input
          value={query}
          onChange={onChange}
          onFocus={() => results.length && setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 180)}
          placeholder="Search a city or exact street address…"
          style={styles.input}
          data-testid="location-search-input"
        />
        {loading && <span style={styles.loadingDot} />}
      </div>
      {open && results.length > 0 && (
        <div style={styles.dropdown} data-testid="location-search-results">
          {results.map((r) => (
            <button key={`${r.lat},${r.lng},${r.name}`} style={styles.result} onMouseDown={() => pick(r)}>
              {r.name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function shortName(display) {
  const parts = display.split(",").map((s) => s.trim());
  if (parts.length <= 2) return display;
  return `${parts[0]}, ${parts[parts.length - 1]}`;
}

const styles = {
  wrap: { position: "relative", marginBottom: 10 },
  inputWrap: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "0 12px",
    background: "#fff",
    border: `1px solid ${colors.border}`,
    borderRadius: 12,
    boxShadow: shadows.soft,
  },
  searchIcon: { fontSize: 16, color: colors.textTertiary },
  input: {
    flex: 1,
    border: "none",
    outline: "none",
    padding: "11px 0",
    fontFamily: fonts.sans,
    fontSize: 13.5,
    color: colors.textPrimary,
    background: "transparent",
  },
  loadingDot: {
    width: 8,
    height: 8,
    borderRadius: 999,
    border: `2px solid rgba(10,132,255,0.25)`,
    borderTopColor: colors.pillar.location,
    animation: "bia-spin 0.8s linear infinite",
  },
  dropdown: {
    position: "absolute",
    top: "calc(100% + 6px)",
    left: 0,
    right: 0,
    background: "#fff",
    border: `1px solid ${colors.border}`,
    borderRadius: 12,
    boxShadow: shadows.float,
    overflow: "hidden",
    zIndex: 30,
    maxHeight: 220,
    overflowY: "auto",
  },
  result: {
    display: "block",
    width: "100%",
    textAlign: "left",
    padding: "10px 12px",
    border: "none",
    borderBottom: `1px solid ${colors.border}`,
    background: "#fff",
    fontFamily: fonts.sans,
    fontSize: 12.5,
    color: colors.textPrimary,
    cursor: "pointer",
    lineHeight: 1.3,
  },
};
